import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getZonalAccess } from '../utils/permissions';
import { provinciaEclesiasticaService, ProvinciaEclesiastica } from '../services/provinciaEclesiasticaService';
import { diocesisService, Diocesis } from '../services/diocesisService';
import { lumisialService } from '../services/lumisialService';
import { Lumisial } from '../lib/supabase';

export interface AdminHierarchyFilters {
  filterProvincia: string;
  filterDiocesis: string;
  filterLumisial: string;
  filterEstado: string;
  filterBusqueda: string;
  setFilterProvincia: (v: string) => void;
  setFilterDiocesis: (v: string) => void;
  setFilterLumisial: (v: string) => void;
  setFilterEstado: (v: string) => void;
  setFilterBusqueda: (v: string) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  isGlobal: boolean;
  isNacional: boolean;
  isDiocesano: boolean;
  provinciasEclesiasticas: ProvinciaEclesiastica[];
  diocesisList: Diocesis[];
  lumisialesList: Lumisial[];
}

export function useAdminHierarchyFilters(): AdminHierarchyFilters {
  const { usuario } = useAuth();
  const zonalAccess = getZonalAccess(usuario);

  const isGlobal = !zonalAccess.provinciaEclesiasticaId;
  const isNacional = !!zonalAccess.provinciaEclesiasticaId && !zonalAccess.diocesisId;
  const isDiocesano = !!zonalAccess.diocesisId;

  const [filterProvincia, setFilterProvincia] = useState<string>(zonalAccess.provinciaEclesiasticaId || '');
  const [filterDiocesis, setFilterDiocesis] = useState<string>(zonalAccess.diocesisId || '');
  const [filterLumisial, setFilterLumisial] = useState<string>('');
  const [filterEstado, setFilterEstado] = useState<string>('');
  const [filterBusqueda, setFilterBusqueda] = useState<string>('');

  const [provinciasEclesiasticas, setProvinciasEclesiasticas] = useState<ProvinciaEclesiastica[]>([]);
  const [diocesisList, setDiocesisList] = useState<Diocesis[]>([]);
  const [lumisialesList, setLumisialesList] = useState<Lumisial[]>([]);

  useEffect(() => {
    if (isGlobal) {
      provinciaEclesiasticaService.getAll().then(setProvinciasEclesiasticas).catch(console.error);
    }
  }, [isGlobal]);

  useEffect(() => {
    const provinciaId = filterProvincia || zonalAccess.provinciaEclesiasticaId;
    if (provinciaId && !isDiocesano) {
      diocesisService.getByProvinciaEclesiastica(provinciaId).then(setDiocesisList).catch(console.error);
    }
  }, [filterProvincia, isDiocesano, zonalAccess.provinciaEclesiasticaId]);

  useEffect(() => {
    const diocesisId = filterDiocesis || zonalAccess.diocesisId;
    if (diocesisId) {
      lumisialService.getByDiocesis(diocesisId).then(setLumisialesList).catch(console.error);
    } else if (filterProvincia || zonalAccess.provinciaEclesiasticaId) {
      const provinciaId = filterProvincia || zonalAccess.provinciaEclesiasticaId!;
      lumisialService.getByProvincia(provinciaId).then(setLumisialesList).catch(console.error);
    }
  }, [filterDiocesis, filterProvincia, zonalAccess.diocesisId, zonalAccess.provinciaEclesiasticaId]);

  const resetFilters = () => {
    if (isGlobal) setFilterProvincia('');
    if (isGlobal || isNacional) setFilterDiocesis('');
    setFilterLumisial('');
    setFilterEstado('');
    setFilterBusqueda('');
  };

  const hasActiveFilters = !!(
    (isGlobal && filterProvincia) ||
    ((isGlobal || isNacional) && filterDiocesis) ||
    filterLumisial ||
    filterEstado ||
    filterBusqueda
  );

  return {
    filterProvincia,
    filterDiocesis,
    filterLumisial,
    filterEstado,
    filterBusqueda,
    setFilterProvincia,
    setFilterDiocesis,
    setFilterLumisial,
    setFilterEstado,
    setFilterBusqueda,
    resetFilters,
    hasActiveFilters,
    isGlobal,
    isNacional,
    isDiocesano,
    provinciasEclesiasticas,
    diocesisList,
    lumisialesList,
  };
}
